/**
 * DND CONDIVISO (@dnd-kit) — sensori e stile di trasformazione riusati da tutti
 * i componenti drag&drop del core (PipelineForm, KanbanBoard). Regola "un solo
 * modo di fare la stessa cosa": la configurazione dei sensori e la traduzione del
 * transform di dnd-kit vivono qui, non ricopiate in ogni componente.
 *
 * Il `transform`/`transition` di dnd-kit è posizionamento dinamico per-frame, non
 * stile visivo: è l'unico caso in cui si applica inline (dnd-kit lo richiede sul
 * nodo trascinabile). Lo stato "in trascinamento" resta invece una classe CSS.
 */

import type { CSSProperties } from 'react';
import {
  PointerSensor,
  KeyboardSensor,
  defaultKeyboardCoordinateGetter,
  useSensor,
  useSensors,
  type KeyboardCoordinateGetter,
} from '@dnd-kit/core';
import { sortableKeyboardCoordinates } from '@dnd-kit/sortable';
import { CSS, type Transform } from '@dnd-kit/utilities';

/**
 * Le frecce: fra gli elementi di una lista ordinabile con `sortableKeyboardCoordinates`; fuori da una
 * lista (le colonne della bacheca, le celle della settimana) quello non sposta niente, e si ripiega sul
 * passo fisso di dnd-kit (25 px a freccia). FILO-41: senza il ripiego la tastiera prendeva l'elemento e
 * non lo muoveva.
 */
const keyboardCoordinates: KeyboardCoordinateGetter = (event, args) =>
  sortableKeyboardCoordinates(event, args) ?? defaultKeyboardCoordinateGetter(event, args);

/** Sensori standard: pointer con soglia di 5px (così un click non avvia un drag)
 *  + tastiera per l'accessibilità. */
export function useDndSensors() {
  return useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: keyboardCoordinates }),
  );
}

/** Stile inline per un nodo sortable: solo posizionamento (transform/transition)
 *  più l'opacità di trascinamento. Usato dai componenti che applicano il transform
 *  alla sorgente (es. liste sortable); chi usa il DragOverlay dima invece la
 *  sorgente via classe `.is-dragging`. */
export function dndTransformStyle(
  transform: Transform | null,
  transition: string | undefined,
  isDragging: boolean,
): CSSProperties {
  return {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.6 : 1,
  };
}
